import { useNavigate } from 'react-router'
import React from 'react'
import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Box, Typography } from '@mui/material'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'

dayjs.extend(utc)
dayjs.extend(timezone)

function getInitials(name) {
  const trimmed = typeof name === 'string' ? name.trim() : ''
  if (!trimmed) return '?'
  const words = trimmed.replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean)
  if (words.length === 0) return trimmed.slice(0, 1).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase()
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
}

function formatRelative(diffMs) {
  const absMs = Math.abs(diffMs)
  const minutes = Math.round(absMs / 60000)
  if (minutes < 1) return null
  let phrase
  if (minutes < 60) {
    phrase = `${minutes} min`
  } else if (minutes < 24 * 60) {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    phrase = rest > 0 && hours < 6 ? `${hours} hr ${rest} min` : `${hours} hr${hours > 1 ? 's' : ''}`
  } else {
    const days = Math.round(minutes / (24 * 60))
    phrase = `${days} day${days > 1 ? 's' : ''}`
  }
  return diffMs > 0 ? `in ${phrase}` : `${phrase} ago`
}

function formatDayLabel(startTime, now) {
  if (!startTime?.isValid?.()) return null
  const startDay = startTime.format('YYYY-MM-DD')
  if (startDay === now.format('YYYY-MM-DD')) return 'Today'
  if (startDay === now.add(1, 'day').format('YYYY-MM-DD')) return 'Tomorrow'
  if (startDay === now.subtract(1, 'day').format('YYYY-MM-DD')) return 'Yesterday'
  return startTime.format('ddd, MMM D')
}

/**
 * Live session-timing status: "Starts 2:00 PM · in 25 min", "Starting now",
 * "Started 2:00 PM · 5 min ago" or "Ended 2:15 PM". Re-renders on a short
 * interval so the countdown stays honest while the visitor waits.
 */
function useSessionTimingStatus(startTime, endTime) {
  const [nowMs, setNowMs] = React.useState(() => Date.now())

  React.useEffect(() => {
    const ticker = setInterval(() => setNowMs(Date.now()), 15000)
    return () => clearInterval(ticker)
  }, [])

  if (!startTime?.isValid?.()) {
    return { headline: null, detail: null, dayLabel: null, phase: 'unknown' }
  }

  const now = dayjs(nowMs)
  const startDiffMs = startTime.valueOf() - nowMs
  const dayLabel = formatDayLabel(startTime, now)
  const startClock = startTime.format('h:mm A')

  if (Math.abs(startDiffMs) < 60000) {
    return { headline: 'Starting now', detail: dayLabel, phase: 'now' }
  }

  if (startDiffMs > 0) {
    return {
      headline: `Starts ${startClock}`,
      detail: [formatRelative(startDiffMs), dayLabel].filter(Boolean).join(' · '),
      phase: 'upcoming',
    }
  }

  if (endTime?.isValid?.() && nowMs > endTime.valueOf() + 60000) {
    return {
      headline: `Ended ${endTime.format('h:mm A')}`,
      detail: [formatRelative(endTime.valueOf() - nowMs), dayLabel].filter(Boolean).join(' · '),
      phase: 'ended',
    }
  }

  return {
    headline: `Started ${startClock}`,
    detail: [formatRelative(startDiffMs), dayLabel].filter(Boolean).join(' · '),
    phase: 'live',
  }
}

export default function JoinPageHeader({ isMountedOnAWebView, userRole, bookedEventFinalData }) {
  const navigate = useNavigate()

  const isOffline = bookedEventFinalData?.meetingSrc === 'offline'
  const startTime = bookedEventFinalData?.startTime
    ? dayjs.utc(bookedEventFinalData.startTime).local()
    : null
  const endTime = bookedEventFinalData?.endTime
    ? dayjs.utc(bookedEventFinalData.endTime).local()
    : null
  const timing = useSessionTimingStatus(startTime, endTime)

  const mentorName = bookedEventFinalData?.mentorName || null
  const eventName = (bookedEventFinalData?.eventName || '').trim()
  const modeLabel = isOffline ? 'In-person visit' : 'Video call'
  // The session type line: "Consultation · Video call". Skip the event name
  // when it just repeats the mode ("Video call").
  const sessionTypeLine =
    eventName && eventName.toLowerCase() !== modeLabel.toLowerCase()
      ? `${eventName} · ${modeLabel}`
      : modeLabel

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      {isMountedOnAWebView && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography
            onClick={() => navigate('/webview/my-live-sessions')}
            sx={{
              color: '#6366f1',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: '#4f46e5' },
            }}
          >
            ← Back to Booked Sessions
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          component="img"
          src={APP_CONSTANTS.APP_LOGO_LINK}
          alt="AUMHUM"
          onError={(e) => {
            e.target.src = logo_brand
          }}
          onClick={() => {
            if (!isMountedOnAWebView) {
              userRole === 'mentor' ? navigate('/mentor-home') : navigate('/')
            }
          }}
          sx={{
            height: { xs: '48px', md: '56px' },
            width: { xs: '48px', md: '56px' },
            borderRadius: '14px',
            cursor: 'pointer',
            boxShadow: '0 4px 18px rgba(99, 102, 241, 0.22)',
            mb: 1,
          }}
        />
        <Typography
          sx={{
            fontSize: '11px',
            color: '#9ca3af',
            fontWeight: 600,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
          }}
        >
          Your Session
        </Typography>
      </Box>

      {bookedEventFinalData && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '460px',
            margin: '0 auto',
            boxSizing: 'border-box',
            background: '#ffffff',
            borderRadius: '20px',
            border: '1px solid rgba(99, 102, 241, 0.12)',
            boxShadow: '0 12px 36px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '4px',
              background: 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%)',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: { xs: '16px 18px', md: '18px 22px' },
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: '52px',
                height: '52px',
                borderRadius: '16px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                color: '#ffffff',
                fontSize: '19px',
                fontWeight: 800,
                letterSpacing: '0.5px',
              }}
            >
              {getInitials(mentorName)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {mentorName && (
                <Typography
                  sx={{
                    fontSize: { xs: '17px', md: '18px' },
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1.3,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {mentorName}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '3px' }}>
                {isOffline ? (
                  <MeetingRoomRoundedIcon sx={{ fontSize: '15px', color: '#16a34a' }} />
                ) : (
                  <VideocamRoundedIcon sx={{ fontSize: '15px', color: '#6366f1' }} />
                )}
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#64748b',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sessionTypeLine}
                </Typography>
              </Box>
            </Box>
          </Box>

          {timing.headline && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: { xs: '0 12px 12px', md: '0 14px 14px' },
                padding: { xs: '12px 14px', md: '14px 16px' },
                boxSizing: 'border-box',
                borderRadius: '14px',
                background:
                  timing.phase === 'live' || timing.phase === 'now'
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)'
                    : 'rgba(99, 102, 241, 0.06)',
              }}
            >
              <ScheduleRoundedIcon
                sx={{
                  fontSize: '22px',
                  color:
                    timing.phase === 'live' || timing.phase === 'now' ? '#16a34a' : '#6366f1',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '16px', md: '17px' },
                    fontWeight: 800,
                    color:
                      timing.phase === 'live' || timing.phase === 'now'
                        ? '#15803d'
                        : '#1e293b',
                    lineHeight: 1.3,
                  }}
                >
                  {timing.headline}
                </Typography>
                {timing.detail && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#64748b',
                      lineHeight: 1.4,
                    }}
                  >
                    {timing.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
